"use client";

import { useState, useCallback, type FormEvent } from "react";
import { useTurnstile } from "@/hooks/useTurnstile";

const inputClass =
  "w-full rounded-lg border border-black/10 bg-neutral-50 px-4 py-3 text-sm text-black placeholder:text-black/30 focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500/30 transition-colors disabled:opacity-50";

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-black/70 mb-1.5">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {hint && <p className="text-xs text-black/40 font-light mb-1.5 -mt-0.5">{hint}</p>}
      {children}
    </div>
  );
}

export default function DeepDiveRequestForm() {
  const { containerRef, token, reset } = useTurnstile();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!token) {
        setError("Please complete the verification.");
        return;
      }

      setSubmitting(true);
      setError(null);

      const fd = new FormData(e.currentTarget);
      const payload = {
        name: String(fd.get("name") || "").trim(),
        email: String(fd.get("email") || "").trim(),
        company: String(fd.get("company") || "").trim(),
        request: String(fd.get("request") || "").trim(),
        dataSources: String(fd.get("dataSources") || "").trim(),
        timePeriod: String(fd.get("timePeriod") || "").trim(),
        columns: String(fd.get("columns") || "").trim(),
        otherRequirements: String(fd.get("otherRequirements") || "").trim(),
        turnstileToken: token,
      };

      try {
        const res = await fetch("/api/leads/deep-dive", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        let data: { error?: string; success?: boolean };
        try {
          data = await res.json();
        } catch {
          data = { error: "Unexpected server error. Please try again." };
        }

        if (!res.ok) {
          setError(data.error || "Something went wrong.");
          reset();
        } else {
          setSuccess(true);
        }
      } catch {
        setError("Network error. Please try again.");
        reset();
      } finally {
        setSubmitting(false);
      }
    },
    [token, reset],
  );

  if (success) {
    return (
      <div className="rounded-xl border border-black/[0.06] bg-black/[0.01] p-8 sm:p-10 text-center">
        <h3 className="text-lg font-semibold tracking-tight mb-2">Request received</h3>
        <p className="text-sm text-black/50 font-light">
          Thanks. We&apos;ve logged your deep dive request and will review the scope.
          We&apos;ll reach out at the email you provided.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-black/[0.06] p-6 sm:p-8 bg-black/[0.01]">
      <p className="text-sm text-black/40 font-light mb-6">
        Tell us what you want analyzed and how. Fields marked with * are required.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name" required>
            <input type="text" name="name" required disabled={submitting} className={inputClass} />
          </Field>
          <Field label="Email" required>
            <input type="email" name="email" required disabled={submitting} className={inputClass} />
          </Field>
        </div>

        <Field label="Company">
          <input type="text" name="company" disabled={submitting} className={inputClass} />
        </Field>

        <Field label="Describe your request" required>
          <textarea
            name="request"
            required
            disabled={submitting}
            rows={4}
            placeholder={'e.g. "Build a quantum computing ecosystem map, comprising private and public players in the US"'}
            className={inputClass + " resize-y"}
          />
        </Field>

        <Field
          label="Detail the data sources and universe"
          hint="e.g. which SEC filings, call transcripts, or web sources to use"
        >
          <textarea
            name="dataSources"
            disabled={submitting}
            rows={3}
            placeholder={'e.g. "Last 3 years of 10-Ks and 20-Fs for companies with market cap over $2B"'}
            className={inputClass + " resize-y"}
          />
        </Field>

        <Field label="Time period" hint="e.g. FY2021–FY2024, or Q1 2023 to present">
          <input type="text" name="timePeriod" disabled={submitting} className={inputClass} />
        </Field>

        <Field label="Define columns" hint="What columns should the output dataset / table have?">
          <textarea
            name="columns"
            disabled={submitting}
            rows={3}
            placeholder="e.g. Period · Revenue · YoY growth · Gross margin · Mgmt commentary"
            className={inputClass + " resize-y"}
          />
        </Field>

        <Field label="Any other requirements">
          <textarea
            name="otherRequirements"
            disabled={submitting}
            rows={3}
            className={inputClass + " resize-y"}
          />
        </Field>

        {/* Turnstile widget */}
        <div ref={containerRef} className="mt-2" />

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-black py-3 text-sm font-medium text-white hover:bg-black/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting ? "Submitting..." : "Submit request"}
        </button>

        <p className="text-xs text-black/30 text-center leading-relaxed">
          Your request goes directly to our research team. Protected by Cloudflare Turnstile.
        </p>
      </form>
    </div>
  );
}
